import { EffectiveDateFilterEnum } from '@app/app.enum';
import EffectiveDateFilters from '@app/components/shared/effectivedate-filters.component';
import MultiSelectField, { IMultiSelectMenuItem } from '@app/components/shared/multi-select-field.component';
import Table from '@app/components/shared/table.component';
import * as models from '@app/models';
import { ICustomerAccountDetailsState } from '@app/models/account.models';
import Injector from '@app/services';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import { Grid } from '@material-ui/core';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedDate } from 'react-intl';
import { Column, UseTableCellProps } from 'react-table';

const intlService: IntlService = Injector.get(IntlService);
const sharedService: SharedService = Injector.get(SharedService);

const AdminAccountRateSetComponent: React.FC<IAdminAccountRateSetProps> = (props) => {
	const [effectiveDateFilter, setEffectiveDateFilter] = useState<Date>(dayjs().toDate());
	const [predefinedEffectiveDate, setPredefinedEffectiveDate] = useState<EffectiveDateFilterEnum>(
		EffectiveDateFilterEnum.Today
	);
	const [rateSet, setRateSet] = useState<models.IRateSet[]>(props.rateSets);

	const menuItems = useMemo((): IMultiSelectMenuItem[] => {
		if (!props.uniqueRateSetCarrier) {
			return [
				{
					text: intlService.GetFormattedMessage('app.common.none-value'),
					value: intlService.GetFormattedMessage('app.common.none-value'),
					selected: true,
					isDefault: true,
				},
			];
		}
		return props.uniqueRateSetCarrier?.map((e, i) => {
			return {
				text: e,
				value: e,
				selected: true,
				isDefault: i === 0 ? true : false
			};
		});
	}, [props.uniqueRateSetCarrier]);
	const [carrierMenuItems, setCarrierMenuItems] = useState<IMultiSelectMenuItem[]>(menuItems);

	const isRateSetIncluded = (rateSetFilter: models.IRateSet, data: string[], effectiveDate: Date) => {
		const startDate = new Date(rateSetFilter.startDate);
		const endDate = new Date(rateSetFilter.endDate);

		return data.includes(rateSetFilter.carrier)
			? predefinedEffectiveDate !== EffectiveDateFilterEnum.All
				? startDate <= effectiveDate && endDate >= effectiveDate
				: true
			: false;
	};

	const filterRateSet = useCallback(() => {
		const arrayOfSelectedCarriers = SharedService.filterAndMap(
			carrierMenuItems,
			(x) => x.selected,
			(x) => x.value
		);
		
		const effectiveDate = new Date(effectiveDateFilter);

		const filteredRateSet = props.rateSets.filter((e) => isRateSetIncluded(e, arrayOfSelectedCarriers, effectiveDate));

		setRateSet(filteredRateSet);
	}, [carrierMenuItems, effectiveDateFilter, predefinedEffectiveDate]);

	useEffect(() => {
		filterRateSet();
	}, [carrierMenuItems, effectiveDateFilter, predefinedEffectiveDate]);

	const getGridColumns = (): Array<Column<models.IRateSet>> => {
		return [
			{
				accessor: 'id',
				Header: intlService.GetFormattedMessage('admin.account-customer-rateset-id-table-header'),
				width: 150,
				disableSortBy: true,
			},
			{
				accessor: 'name',
				Header: intlService.GetFormattedMessage('admin.account-customer-rateset-name-table-header'),
				width: 350,
				disableSortBy: true,
			},
			{
				accessor: 'carrier',
				Header: intlService.GetFormattedMessage('admin.account-customer-rateset-carrier-table-header'),
				width: 200,
				disableSortBy: true,
			},
			{
				accessor: 'rateSetType',
				Header: intlService.GetFormattedMessage('admin.account-customer-rateset-type-table-header'),
				width: 200,
				disableSortBy: true,
			},
			{
				accessor: 'startDate',
				Header: intlService.GetFormattedMessage('admin.account-customer-rateset-startDate-table-header'),
				Cell: (tableData: UseTableCellProps<models.IRateSet>) => (
					<>
						<FormattedDate value={sharedService.getNewDateIfNotExist(tableData.value)} />
					</>
				),
				width: 200,
				disableSortBy: true,
			},
			{
				accessor: 'endDate',
				Header: intlService.GetFormattedMessage('admin.account-customer-rateset-endDate-table-header'),
				Cell: (tableData: UseTableCellProps<models.IRateSet>) => (
					<>
						<FormattedDate value={sharedService.getNewDateIfNotExist(tableData.value)} />
					</>
				),
				width: 200,
				disableSortBy: true,
			},
			{
				accessor: 'isInternational',
				Header: intlService.GetFormattedMessage('admin.account-customer-rateset-isInternational-table-header'),
				width: 200,
				disableSortBy: true,
				Cell: (tableDate: UseTableCellProps<models.IRateSet>) => <>{tableDate.value ? 'true' : 'false'}</>,
			},
		];
	};

	const handleCarrierFilterOnSave = (selectedValues: string[]) => {
		const updatedMenuItems = carrierMenuItems.slice();

		carrierMenuItems.forEach((item, i) => {
			updatedMenuItems[i].selected = selectedValues.includes(item.value);
		});

		setCarrierMenuItems(updatedMenuItems);
	};

	const handleOnDateChange = (startDate: Date, filterValue: EffectiveDateFilterEnum) => {
		setPredefinedEffectiveDate(filterValue);
		setEffectiveDateFilter(startDate);
	};

	const getFilterRow = () => {
		return (
			<>
				<Grid container={true} spacing={2}>
					<Grid item={true} lg={8} md={12} sm={12} xs={12}>
						<Grid container={true} justifyContent='flex-start' spacing={2}>
							<Grid item={true} lg={4} md={4} sm={12} xs={12}>
								<EffectiveDateFilters
									effectiveDate={effectiveDateFilter}
									onDateChange={handleOnDateChange}
									disabled={props.rateSetdataLoading}
								/>
							</Grid>
							<Grid item={true} lg={4} md={4} sm={12} xs={12}>
								<MultiSelectField
									id='carrierFilter'
									label={intlService.GetFormattedMessage('admin.account-customer-carrier-filter-label')}
									menuItems={carrierMenuItems}
									onSave={handleCarrierFilterOnSave}
									name='carrierFilter'
									disabled={props.rateSetdataLoading || props.rateSets.length === 0}
									saveButtonText={intlService.GetFormattedMessage('app.filter-done')}
								/>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</>
		);
	};

	return (
		<div>
			<Grid container={true}>
				{getFilterRow()}
				<Table<models.IRateSet>
					columns={getGridColumns()}
					data={rateSet ?? []}
					rateSetdataLoading={props.rateSetdataLoading}
					applyRowColor={true}
				/>
			</Grid>
		</div>
	);
};

export interface IAdminAccountRateSetProps
	extends Pick<ICustomerAccountDetailsState, 'uniqueRateSetCarrier' | 'rateSets' | 'buttonLoading' | 'rateSetdataLoading'> {}

export default AdminAccountRateSetComponent;



// WEBPACK FOOTER //
// ./src/components/admin/customer/admin-account-rateset.component.tsx